import { differenceInCalendarDays } from 'date-fns';

import type { BadgeColor } from '@kc-console/common-ui';
import type { AnnouncementListEntry } from './types';

/** 게시일 표시 포맷 (예: 2026.04.27 (월)) */
export const DATE_FORMAT = 'yyyy.MM.dd (EEE)';

/** 한 페이지에 표시할 새소식 수 */
export const PAGE_SIZE = 3;

/** New 뱃지 노출 기준일 (게시일로부터 N일 이내) */
export const NEW_THRESHOLD_DAYS = 7;

/**
 * 새소식 Badge 에 사용할 수 있는 컬러 목록입니다.
 * @description
 * - 디자인 가이드에 따라 Red, Black, Gray 계열(red, black, gray, lightGray, inverse)은 제외합니다.
 */
const ANNOUNCEMENT_BADGE_COLORS: BadgeColor[] = [
  'green',
  'yellow',
  'blue',
  'purple',
  'magenta',
  'sky',
  'lightGreen',
  'orange',
];

/** 새소식 Badge 컬러를 찾지 못했을 때 사용하는 기본 색상 */
export const DEFAULT_ANNOUNCEMENT_BADGE_COLOR: BadgeColor = ANNOUNCEMENT_BADGE_COLORS[0];

/**
 * 게시일이 오늘로부터 달력일 기준 7일 이내인지 판별합니다.
 * @description
 * - New 뱃지 노출 조건이며, now 인자는 테스트 주입용입니다.
 */
export const isNewAnnouncement = (publishedAt: string | null, now: Date = new Date()): boolean => {
  if (!publishedAt) return false;

  const days = differenceInCalendarDays(now, new Date(publishedAt));
  return days >= 0 && days <= NEW_THRESHOLD_DAYS;
};

/**
 * 특정 새소식이 포함된 0-based 페이지 번호를 계산합니다.
 * @description
 * - 목록에서 id 를 찾지 못하면 첫 페이지(0)를 반환합니다.
 */
export const getInitialPage = (
  announcements: AnnouncementListEntry[],
  targetId: string,
  pageSize: number,
): number => {
  const index = announcements.findIndex(announcement => announcement.id === targetId);
  return index < 0 ? 0 : Math.floor(index / pageSize);
};

/**
 * 새소식 목록의 subTitle 별 Badge 컬러 맵을 생성합니다.
 * @description
 * - 오래된 순(목록 역순)으로 subTitle 첫 등장 순서대로 팔레트를 배정합니다.
 * - 이렇게 하면 새 소식이 추가돼도 기존 subTitle 의 뱃지 컬러가 그대로 유지됩니다.
 * - subTitle 종류가 8개 이하면 중복이 발생하지 않고, 8종을 넘으면 팔레트를 순환하며 재사용합니다.
 */
export const createAnnouncementBadgeColorMap = (
  announcements: Pick<AnnouncementListEntry, 'subTitle'>[],
) => {
  const colorMap = new Map<AnnouncementListEntry['subTitle'], BadgeColor>();

  [...announcements].reverse().forEach(({ subTitle }) => {
    if (colorMap.has(subTitle)) return;

    colorMap.set(
      subTitle,
      ANNOUNCEMENT_BADGE_COLORS[colorMap.size % ANNOUNCEMENT_BADGE_COLORS.length],
    );
  });

  return colorMap;
};
